/**
 * Header target/offset chip — shows the currently selected prism/probe
 * so the surveyor always knows what the app is correcting for.
 *
 * Clicking the chip opens a popover with:
 *   • The preset dropdown (Reflectorless, GPR1, Ball 25.4mm ⊥ 30mm, …)
 *   • Live fields for the Custom preset (constant, ⊥ mm, pole height)
 *   • "Send to instrument" button that writes BAP_SetTargetType to the TS12
 *
 * The chip auto-refreshes when the setting is changed elsewhere (e.g. the
 * user hit "Send to instrument" from a different tab).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Target as TargetIcon, CaretDown, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { target as targetApi, station } from "../lib/api";

export const TargetChip = () => {
  const [presets, setPresets] = useState([]);
  const [setting, setSetting] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const popoverRef = useRef(null);

  const active = useMemo(() => {
    if (!setting) return null;
    if (setting.preset_id === "custom") return setting.custom;
    return presets.find((p) => p.id === setting.preset_id) || presets[0];
  }, [presets, setting]);

  // Bootstrap
  useEffect(() => {
    Promise.all([targetApi.presets(), targetApi.get()])
      .then(([ps, s]) => {
        setPresets(ps);
        setSetting(s);
      })
      .catch(() => {});
  }, []);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const pickPreset = async (id) => {
    setBusy(true);
    try {
      const updated = await targetApi.set({ preset_id: id });
      setSetting(updated);
      const picked =
        id === "custom" ? updated.custom : presets.find((p) => p.id === id);
      toast.success(`Target: ${picked?.label || id}`);

      // Push the target-type change down to the TS12 itself
      // (BAP_SetTargetType) so the instrument's own mode matches what
      // the app is correcting for. Fire-and-forget — errors here should
      // never block the UI.
      if (picked && Number.isInteger(picked.bap_target_type)) {
        station.setTargetType(picked.bap_target_type).catch(() => undefined);
      }

      // Prism modes → auto-trigger Power Search so the TS acquires
      // the prism without the surveyor babysitting the scope.
      if (picked && picked.kind !== "reflectorless") {
        toast("Power search — hunting for prism…", { duration: 3000 });
        station.powerSearch()
          .then(() => toast.success("Prism locked"))
          .catch(() => toast.error("Power search failed — aim manually"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save target");
    } finally {
      setBusy(false);
    }
  };

  const updateCustom = async (patch) => {
    if (!setting) return;
    const merged = { ...setting.custom, ...patch };
    setBusy(true);
    try {
      const updated = await targetApi.set({
        preset_id: "custom",
        custom: merged,
      });
      setSetting(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save custom");
    } finally {
      setBusy(false);
    }
  };

  if (!active) {
    return (
      <div className="flex items-center gap-1 rounded-sm border border-[var(--border)] px-2 py-1 text-[10px] uppercase tracking-widest text-[var(--text-mute)]">
        <TargetIcon size={11} weight="bold" /> loading…
      </div>
    );
  }

  // Chip appearance changes with the correction magnitude — a big ⊥ offset
  // deserves a warning-coloured chip so the surveyor is very aware.
  const isCorrecting = (active.perpendicular_mm || 0) > 0
                     || (active.pole_height_m || 0) > 0;
  const chipColor = active.kind === "reflectorless"
    ? "text-[var(--text-dim)] border-[var(--border)]"
    : isCorrecting
      ? "text-[var(--amber)] border-[var(--amber)]"
      : "text-[var(--primary)] border-[var(--primary)]";

  return (
    <div className="relative" ref={popoverRef}>
      <button
        data-testid="target-chip"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-sm border bg-[var(--surface-2)] px-2 py-1 text-[10px] uppercase tracking-widest transition-colors hover:bg-[var(--surface-3)] ${chipColor}`}
        title="Target / offset device"
      >
        <TargetIcon size={11} weight="bold" />
        <span className="font-mono">{active.label}</span>
        <CaretDown size={9} weight="bold" className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div
          data-testid="target-popover"
          className="absolute right-0 top-full z-50 mt-1 w-80 rounded-sm border border-[var(--border)] bg-[var(--surface-1)] p-3 shadow-lg"
        >
          <div className="mb-2 text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
            Target / offset device
          </div>
          <ul className="mb-3 divide-y divide-[var(--border)] rounded-sm border border-[var(--border)]">
            {presets.map((p) => (
              <li key={p.id}>
                <button
                  data-testid={`target-preset-${p.id}`}
                  onClick={() => pickPreset(p.id)}
                  disabled={busy}
                  className={`flex w-full items-center justify-between px-2 py-1.5 text-left text-[11px] transition-colors ${
                    setting.preset_id === p.id
                      ? "bg-[var(--primary)]/20 text-[var(--primary)]"
                      : "hover:bg-[var(--surface-2)]"
                  }`}
                >
                  <span className="font-mono">{p.label}</span>
                  <span className="font-mono text-[9px] text-[var(--text-mute)]">
                    {p.constant_mm !== 0 && `${p.constant_mm > 0 ? "+" : ""}${p.constant_mm.toFixed(1)}mm`}
                    {p.perpendicular_mm !== 0 && `  ⊥${p.perpendicular_mm.toFixed(1)}`}
                    {p.pole_height_m !== 0 && `  ↕${p.pole_height_m.toFixed(2)}m`}
                  </span>
                </button>
              </li>
            ))}
            <li>
              <button
                data-testid="target-preset-custom"
                onClick={() => pickPreset("custom")}
                disabled={busy}
                className={`flex w-full items-center justify-between px-2 py-1.5 text-left text-[11px] transition-colors ${
                  setting.preset_id === "custom"
                    ? "bg-[var(--primary)]/20 text-[var(--primary)]"
                    : "hover:bg-[var(--surface-2)]"
                }`}
              >
                <span className="font-mono">Custom</span>
                <span className="font-mono text-[9px] text-[var(--text-mute)]">edit below</span>
              </button>
            </li>
          </ul>

          {setting.preset_id === "custom" && (
            <div className="mb-2 rounded-sm border border-[var(--border)] p-2">
              <div className="mb-1 text-[9px] uppercase tracking-widest text-[var(--text-dim)]">
                Custom target
              </div>
              <div className="grid grid-cols-2 gap-2">
                <NumField
                  label="Axial mm"
                  value={setting.custom.constant_mm}
                  onCommit={(v) => updateCustom({ constant_mm: v })}
                  hint="prism constant"
                  testid="custom-constant"
                />
                <NumField
                  label="⊥ to surface mm"
                  value={setting.custom.perpendicular_mm}
                  onCommit={(v) => updateCustom({ perpendicular_mm: v })}
                  hint="ball offset"
                  testid="custom-perpendicular"
                />
                <NumField
                  label="Pole height m"
                  value={setting.custom.pole_height_m}
                  onCommit={(v) => updateCustom({ pole_height_m: v })}
                  hint="vertical mount"
                  testid="custom-pole"
                />
              </div>
            </div>
          )}

          <div className="rounded-sm border border-[var(--border)] bg-[var(--surface-2)] px-2 py-1.5 text-[10px] leading-tight text-[var(--text-dim)]">
            <b className="text-[var(--text)]">Applied per feature:</b><br />
            {(active.constant_mm || 0) !== 0 && <>
              • Axial <span className="font-mono text-[var(--text)]">{active.constant_mm > 0 ? "+" : ""}{active.constant_mm}mm</span> → written to TS12 (BAP_SetTargetType)<br />
            </>}
            {(active.perpendicular_mm || 0) !== 0 && <>
              • Perpendicular <span className="font-mono text-[var(--text)]">{active.perpendicular_mm}mm</span> → subtracted along fitted surface normal (needs ≥3 shots on same face)<br />
            </>}
            {(active.pole_height_m || 0) !== 0 && <>
              • Pole <span className="font-mono text-[var(--text)]">{active.pole_height_m}m</span> → subtracted from Z<br />
            </>}
            {!isCorrecting && active.kind !== "reflectorless" && (
              <span>• Only the TS12 prism constant is applied.</span>
            )}
            {active.kind === "reflectorless" && (
              <span>• No offset — laser hits the surface directly.</span>
            )}
          </div>

          {isCorrecting && (
            <div className="mt-2 flex items-start gap-1 rounded-sm border border-[var(--amber)] bg-black/40 px-2 py-1 text-[10px] leading-tight text-[var(--amber)]">
              <Warning size={11} weight="bold" className="mt-0.5 flex-shrink-0" />
              <span>
                Changing target mid-session affects every fit. Existing
                shots are re-corrected automatically on the next refresh.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const NumField = ({ label, value, onCommit, hint, testid }) => {
  const [v, setV] = useState(String(value ?? 0));
  useEffect(() => setV(String(value ?? 0)), [value]);
  return (
    <label className="flex flex-col gap-0.5 text-[9px] uppercase tracking-widest text-[var(--text-dim)]">
      {label}
      <input
        data-testid={testid}
        type="number"
        step="0.1"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          const n = parseFloat(v);
          if (Number.isFinite(n)) onCommit(n);
        }}
        className="rounded-sm border border-[var(--border)] bg-[var(--surface-2)] px-1.5 py-1 font-mono text-[11px]"
      />
      {hint && <span className="text-[8px] text-[var(--text-mute)]">{hint}</span>}
    </label>
  );
};
